import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Plus, ChevronLeft, ChevronRight, CalendarDays, History as HistoryIcon, HardHat, FolderKanban, ClipboardCheck, Zap, AlertCircle, Play, CheckCircle2, Circle } from "lucide-react";
import CalendarJobDialog from "@/components/CalendarJobDialog";
import {
  WEEKDAYS_TR, MONTHS_TR, isoDate, addDays, monthGrid,
  computeWeekSegments, colorForEntry,
} from "@/lib/calendarUtils";

const MAX_LANES = 3; // visible bar rows per day before overflow chip

const PRIORITY_LABEL = { normal: "Normal", high: "Yüksek", urgent: "Acil" };
const PRIORITY_CLS = {
  normal: "bg-zinc-100 text-zinc-600 border-zinc-200",
  high: "bg-orange-50 text-orange-700 border-orange-200",
  urgent: "bg-red-50 text-red-700 border-red-200",
};

function fmtDateFull(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getDate()} ${MONTHS_TR[d.getMonth()]} ${d.getFullYear()}, ${WEEKDAYS_TR[(d.getDay() + 6) % 7]}`;
}

// One week row rendered as a CSS grid with lane rows.
function WeekRow({ weekDates, entries, monthIndex, todayIso, selectedIso, onSelectDay, onOpenEntry }) {
  const segments = useMemo(() => computeWeekSegments(entries, weekDates), [entries, weekDates]);

  // Group per-day overflow counts (segments with lane >= MAX_LANES that touch each day)
  const overflowByCol = Array(7).fill(0);
  for (const s of segments) {
    if (s.lane >= MAX_LANES) {
      for (let c = s.startCol; c <= s.endCol; c++) overflowByCol[c] += 1;
    }
  }

  // Row template: header row + MAX_LANES lane rows + overflow row
  const rowStyle = {
    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
    gridTemplateRows: `28px repeat(${MAX_LANES}, 22px) 20px`,
  };

  return (
    <div className="grid relative" style={rowStyle}>
      {/* Background day cells spanning all rows in a column */}
      {weekDates.map((d, col) => {
        const iso = isoDate(d);
        const inMonth = d.getMonth() === monthIndex;
        const isToday = iso === todayIso;
        const isSelected = iso === selectedIso;
        const isWeekend = col >= 5;
        const cls = [
          "border-t border-l border-zinc-200 cursor-pointer transition",
          !inMonth ? "bg-zinc-50/60" : isWeekend ? "bg-zinc-50/30" : "bg-white",
          isSelected ? "ring-1 ring-inset ring-zinc-900" : "",
          col === 6 ? "border-r" : "",
        ].join(" ");
        return (
          <div
            key={`bg-${iso}`}
            role="button"
            tabIndex={0}
            data-testid={`day-${iso}`}
            style={{ gridColumn: col + 1, gridRow: `1 / -1` }}
            className={cls}
            onClick={() => onSelectDay(iso)}
          >
            <div className="flex items-center justify-between px-2 pt-1">
              <span className={[
                "text-xs font-medium",
                !inMonth ? "text-zinc-400" : "text-zinc-700",
                isToday ? "inline-flex items-center justify-center w-6 h-6 rounded-full bg-zinc-900 text-white" : "",
              ].join(" ")}>{d.getDate()}</span>
              {col === 0 && inMonth && (
                <span className="text-[10px] uppercase text-zinc-400 hidden md:inline">{MONTHS_TR[monthIndex].slice(0, 3)}</span>
              )}
            </div>
          </div>
        );
      })}

      {/* Segment bars */}
      {segments.filter(s => s.lane < MAX_LANES).map((s, idx) => {
        const c = colorForEntry(s.entry);
        const isFree = s.entry.kind === "free";
        const cornerL = s.continuesLeft ? "rounded-l-none border-l-0" : "";
        const cornerR = s.continuesRight ? "rounded-r-none border-r-0" : "";
        return (
          <div
            key={`seg-${s.entry.id}-${s.startCol}-${idx}`}
            data-testid={`entry-bar-${s.entry.id}`}
            style={{
              gridColumn: `${s.startCol + 1} / span ${s.length}`,
              gridRow: s.lane + 2,
              background: c.bg,
              borderColor: c.border,
              color: c.text,
            }}
            className={`mx-0.5 my-0.5 px-1.5 py-0 text-[11px] leading-[20px] border rounded whitespace-nowrap overflow-hidden text-ellipsis cursor-pointer hover:opacity-90 flex items-center gap-1 ${cornerL} ${cornerR}`}
            onClick={(e) => { e.stopPropagation(); onOpenEntry(s.entry); }}
            title={s.entry.title}
          >
            {isFree
              ? <Circle size={9} className="shrink-0" style={{ color: c.accent }} />
              : <Circle size={9} className="shrink-0 fill-current" style={{ color: c.accent }} />}
            <span className="truncate">{s.entry.title}</span>
            {s.entry.priority === "urgent" && <Zap size={10} className="shrink-0 text-red-600" />}
          </div>
        );
      })}

      {/* +N daha */}
      {overflowByCol.map((n, col) => n > 0 && (
        <div
          key={`ovf-${col}`}
          style={{ gridColumn: col + 1, gridRow: MAX_LANES + 2 }}
          className="mx-0.5 text-[10px] text-zinc-600 hover:text-zinc-900 cursor-pointer px-1.5 truncate"
          onClick={() => onSelectDay(isoDate(weekDates[col]))}
        >
          +{n} daha
        </div>
      ))}
    </div>
  );
}

// History-mode overlay: renders takeover/finish pills inside a week row.
function WeekRowHistory({ weekDates, events, monthIndex, todayIso, selectedIso, onSelectDay }) {
  const byCol = Array.from({ length: 7 }, () => []);
  for (const e of events) {
    const idx = weekDates.findIndex(d => isoDate(d) === e.date);
    if (idx >= 0) byCol[idx].push(e);
  }
  const maxRows = Math.max(3, ...byCol.map(a => a.length));

  return (
    <div
      className="grid relative"
      style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gridTemplateRows: `28px repeat(${Math.min(maxRows, 3)}, 22px) ${maxRows > 3 ? "20px" : "0px"}` }}
    >
      {weekDates.map((d, col) => {
        const iso = isoDate(d);
        const inMonth = d.getMonth() === monthIndex;
        const isToday = iso === todayIso;
        const isSelected = iso === selectedIso;
        const cls = [
          "border-t border-l border-zinc-200 cursor-pointer transition",
          !inMonth ? "bg-zinc-50/60" : "bg-white",
          isSelected ? "ring-1 ring-inset ring-zinc-900" : "",
          col === 6 ? "border-r" : "",
        ].join(" ");
        return (
          <div
            key={`bg-h-${iso}`}
            role="button"
            tabIndex={0}
            data-testid={`day-history-${iso}`}
            style={{ gridColumn: col + 1, gridRow: `1 / -1` }}
            className={cls}
            onClick={() => onSelectDay(iso)}
          >
            <div className="flex items-center justify-between px-2 pt-1">
              <span className={[
                "text-xs font-medium",
                !inMonth ? "text-zinc-400" : "text-zinc-700",
                isToday ? "inline-flex items-center justify-center w-6 h-6 rounded-full bg-zinc-900 text-white" : "",
              ].join(" ")}>{d.getDate()}</span>
            </div>
          </div>
        );
      })}
      {byCol.map((events, col) => events.slice(0, 3).map((ev, i) => {
        const isStart = ev.event === "takeover";
        const cls = isStart
          ? "bg-blue-50 border-blue-300 text-blue-800"
          : ev.completed
            ? "bg-emerald-50 border-emerald-300 text-emerald-800"
            : "bg-amber-50 border-amber-300 text-amber-800";
        return (
          <div
            key={`ev-${ev.log_id}-${ev.event}`}
            style={{ gridColumn: col + 1, gridRow: i + 2 }}
            className={`mx-0.5 my-0.5 px-1.5 text-[10.5px] leading-[20px] border rounded truncate ${cls}`}
            title={`${ev.user_name} · ${ev.item_name || ""} · ${ev.stage || ""}`}
          >
            {isStart
              ? <Play size={9} className="inline mr-0.5 -mt-0.5" />
              : <CheckCircle2 size={9} className="inline mr-0.5 -mt-0.5" />}
            {ev.user_name} · {ev.stage || "-"}
          </div>
        );
      }))}
      {byCol.map((events, col) => events.length > 3 && (
        <div
          key={`ev-ovf-${col}`}
          style={{ gridColumn: col + 1, gridRow: 5 }}
          className="mx-0.5 text-[10px] text-zinc-600 hover:text-zinc-900 cursor-pointer px-1.5 truncate"
          onClick={() => onSelectDay(isoDate(weekDates[col]))}
        >
          +{events.length - 3} olay
        </div>
      ))}
    </div>
  );
}

function DetailPanel({ dateIso, historyMode, entries, events, onEdit, isAdmin }) {
  if (!dateIso) {
    return (
      <div className="text-sm text-zinc-500 p-4">Sağdaki detayları görmek için bir gün seçin.</div>
    );
  }
  const dayEntries = entries.filter(e => (e.scheduled_dates || []).includes(dateIso));
  const dayEvents = events.filter(e => e.date === dateIso);

  return (
    <div className="space-y-4">
      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-500">Seçilen Gün</div>
        <h3 className="text-lg font-semibold mt-0.5">{fmtDateFull(dateIso)}</h3>
      </div>

      {!historyMode && (
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
            <ClipboardCheck size={12} /> Planlanan İşler ({dayEntries.length})
          </div>
          {dayEntries.length === 0 ? (
            <div className="text-sm text-zinc-500">Bu gün için planlanmış iş yok.</div>
          ) : (
            <div className="space-y-2">
              {dayEntries.map(e => {
                const c = colorForEntry(e);
                return (
                  <div
                    key={e.id}
                    className="rounded-md border p-2 cursor-pointer hover:shadow-sm transition"
                    style={{ background: c.bg, borderColor: c.border }}
                    onClick={() => isAdmin && onEdit(e)}
                    data-testid={`day-entry-${e.id}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="text-xs text-zinc-600 flex items-center gap-1">
                          {e.kind === "assignment"
                            ? <><FolderKanban size={11} /><span className="truncate">{e.project_name}</span></>
                            : <><CalendarDays size={11} /><span>Serbest iş</span></>}
                        </div>
                        <div className="font-semibold text-sm mt-0.5 truncate" style={{ color: c.text }}>{e.title}</div>
                        {e.customer_name && (
                          <div className="text-[11px] text-zinc-600 truncate">{e.customer_name}</div>
                        )}
                      </div>
                      <div className="flex flex-col items-end gap-1 shrink-0">
                        <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] border ${PRIORITY_CLS[e.priority] || PRIORITY_CLS.normal}`}>
                          {e.priority === "urgent" && <Zap size={9} className="inline mr-0.5" />}
                          {PRIORITY_LABEL[e.priority] || "Normal"}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5 items-center text-[11px]">
                      {e.stage && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/70 border border-white/40"><HardHat size={10} /> {e.stage}</span>}
                      {e.user_name
                        ? <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/70 border border-white/40">{e.user_name}</span>
                        : <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-white/70 border border-white/40 text-zinc-500">Genel</span>}
                      {e.status === "done" && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"><CheckCircle2 size={10} /> Tamam</span>}
                      {e.status === "cancelled" && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-500 border border-zinc-200">İptal</span>}
                    </div>
                    {e.notes && <div className="mt-1.5 text-[11px] whitespace-pre-wrap text-zinc-700 line-clamp-3">{e.notes}</div>}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {historyMode && (
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1">
            <HistoryIcon size={12} /> Usta Hareketleri ({dayEvents.length})
          </div>
          {dayEvents.length === 0 ? (
            <div className="text-sm text-zinc-500">Bu güne ait devralma/bırakma kaydı yok.</div>
          ) : (
            <div className="space-y-2">
              {dayEvents.map(ev => (
                <div
                  key={`${ev.log_id}-${ev.event}`}
                  className={`rounded-md border p-2 text-xs ${ev.event === "takeover" ? "bg-blue-50 border-blue-200" : ev.completed ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`}
                >
                  <div className="flex items-center gap-1 mb-1 font-medium">
                    {ev.event === "takeover"
                      ? <><Play size={10} /> Devraldı</>
                      : <><CheckCircle2 size={10} /> {ev.completed ? "İşi bitirdi" : "Bıraktı / Sonraki aşama"}</>}
                    <span className="ml-1 text-zinc-500 font-normal">{new Date(ev.ts).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}</span>
                  </div>
                  <div className="font-semibold">{ev.user_name}</div>
                  <div className="text-zinc-600">{ev.item_name || "Ürün"} — {ev.stage || "-"}</div>
                  {ev.project_name && <div className="text-[11px] text-zinc-500 mt-0.5"><FolderKanban size={9} className="inline mr-0.5" />{ev.project_name} · {ev.customer_name}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Calendar() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const today = new Date();
  const todayIso = isoDate(today);

  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedIso, setSelectedIso] = useState(todayIso);
  const [historyMode, setHistoryMode] = useState(false);

  const [entries, setEntries] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);
  const [defaultDate, setDefaultDate] = useState(null);

  const grid = useMemo(() => monthGrid(cursor.getFullYear(), cursor.getMonth()), [cursor]);
  const rangeStart = isoDate(grid.gridStart);
  const rangeEnd = isoDate(addDays(grid.gridStart, 41));

  const load = async () => {
    setLoading(true);
    try {
      if (!historyMode) {
        const r = await api.get("/calendar/entries", { params: { start: rangeStart, end: rangeEnd } });
        setEntries(r.data);
        setEvents([]);
      } else {
        const r = await api.get("/calendar/history", { params: { start: rangeStart, end: rangeEnd } });
        setEvents(r.data);
        setEntries([]);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [rangeStart, rangeEnd, historyMode]);

  const goPrev = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const goNext = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));
  const goToday = () => {
    setCursor(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedIso(todayIso);
  };

  const onSelectDay = (iso) => setSelectedIso(iso);
  const onOpenEntry = (entry) => {
    if (!isAdmin) { setSelectedIso(entry.scheduled_dates?.[0] || selectedIso); return; }
    setEditEntry(entry);
    setDefaultDate(null);
    setDialogOpen(true);
  };
  const onNew = () => {
    if (!isAdmin) return;
    setEditEntry(null);
    setDefaultDate(selectedIso || todayIso);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-4" data-testid="calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500">Planlama</div>
          <h1 className="text-3xl mt-1 flex items-center gap-3">
            <CalendarDays size={28} className="text-zinc-500" />
            Takvim
          </h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 pr-3 border-r border-zinc-200">
            <Switch id="history-mode" checked={historyMode} onCheckedChange={setHistoryMode} data-testid="history-mode-switch" />
            <Label htmlFor="history-mode" className="text-sm cursor-pointer select-none">
              <HistoryIcon size={13} className="inline mr-1" />
              Geçmiş Modu {historyMode ? "· Açık" : "· Kapalı"}
            </Label>
          </div>
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={goPrev} data-testid="cal-prev"><ChevronLeft size={16} /></Button>
            <Button variant="ghost" size="sm" onClick={goToday} data-testid="cal-today">Bugün</Button>
            <Button variant="ghost" size="sm" onClick={goNext} data-testid="cal-next"><ChevronRight size={16} /></Button>
          </div>
          <div className="text-sm font-semibold w-40 text-center">
            {MONTHS_TR[cursor.getMonth()]} {cursor.getFullYear()}
          </div>
          {isAdmin && (
            <Button onClick={onNew} className="bg-zinc-900 hover:bg-zinc-800" data-testid="calendar-new-button">
              <Plus size={16} className="mr-1" />Yeni Planlama
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,3fr)_minmax(260px,1fr)] gap-4">
        <Card className="p-0 border border-zinc-200 shadow-none overflow-hidden">
          {/* Weekday header */}
          <div className="grid grid-cols-7 text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-50 border-b border-zinc-200">
            {WEEKDAYS_TR.map((wd, i) => (
              <div key={wd} className={`px-2 py-2 ${i >= 5 ? "text-zinc-400" : ""}`}>{wd}</div>
            ))}
          </div>
          {loading ? (
            <div className="p-8 text-center text-zinc-500 text-sm">Yükleniyor...</div>
          ) : (
            <div>
              {grid.weeks.map((weekDates, wi) => (
                historyMode
                  ? <WeekRowHistory
                      key={`w-${wi}`}
                      weekDates={weekDates}
                      events={events}
                      monthIndex={cursor.getMonth()}
                      todayIso={todayIso}
                      selectedIso={selectedIso}
                      onSelectDay={onSelectDay}
                    />
                  : <WeekRow
                      key={`w-${wi}`}
                      weekDates={weekDates}
                      entries={entries}
                      monthIndex={cursor.getMonth()}
                      todayIso={todayIso}
                      selectedIso={selectedIso}
                      onSelectDay={onSelectDay}
                      onOpenEntry={onOpenEntry}
                    />
              ))}
              <div className="border-b border-zinc-200" />
            </div>
          )}
        </Card>

        <Card className="p-4 border border-zinc-200 shadow-none min-w-0" data-testid="calendar-detail-panel">
          <DetailPanel
            dateIso={selectedIso}
            historyMode={historyMode}
            entries={entries}
            events={events}
            onEdit={(e) => { setEditEntry(e); setDefaultDate(null); setDialogOpen(true); }}
            isAdmin={isAdmin}
          />
        </Card>
      </div>

      {isAdmin && (
        <CalendarJobDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          editEntry={editEntry}
          defaultDate={defaultDate}
          onSaved={() => load()}
        />
      )}

      {!historyMode && (
        <div className="text-[11px] text-zinc-500 flex flex-wrap items-center gap-3 mt-1">
          <span className="inline-flex items-center gap-1"><AlertCircle size={11} /> Ard arda gelen çoklu-günlü işler tek bar olarak birleşir. Farklı haftalarda ayrı çizilir.</span>
          <span className="inline-flex items-center gap-1"><Zap size={11} className="text-red-600" /> Acil öncelik</span>
        </div>
      )}
    </div>
  );
}
